import { noop } from "lodash";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Button, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { prepareMailboxAddress } from "helpers/mailboxes";
import { useAgentFlag } from "hooks/use-agent-flag";
import { selectLicenseId } from "store/entities/license";
import { selectCanManageCustomMailboxes } from "store/entities/mailboxes/selectors";
import { SettingsViewActions, selectNextForwardingAddress } from "store/views/settings";
import { withVar } from "themes/helpers";

import { NotAllowedBanner } from "../NotAllowedBanner";
import { trackEmailSectionOpened } from "../analytics";
import {
  EmailSettingsRoutes,
  EmailSettingsSubsection,
  FORWARDING_INSTRUCTIONS_HASH,
  FORWARDING_SETUP_HASH,
} from "../routes";

import DeleteInboxModal from "./DeleteInboxModal";
import { ForwardingAddressesTip } from "./ForwardingAddressesTip";
import { ForwardingInfo } from "./ForwardingInfo";
import {
  trackAddModalOpened,
  trackDeleteModalOpened,
  trackEditModalOpened,
  trackInstructionsOpened,
} from "./analytics";
import { ForwardingAddressList } from "./forwarding-address-list/ForwardingAddressList";
import InstructionsModal from "./instructions-modal/InstructionsModal";
import SetupInboxModal from "./setup-inbox-modal/SetupInboxModal";

export const ForwardingAddressesSection: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { hash } = history.location;

  const [hasClosedTip] = useAgentFlag("hasClosedForwardingAddressesTip");

  const licenseId = useSelector(selectLicenseId);
  const nextEmail = useSelector(selectNextForwardingAddress);
  const canManageInboxes = useSelector(selectCanManageCustomMailboxes);

  const [instructionsModalState, setInstructionsModal] = useState<string | boolean>(false);
  const [setupModalState, setSetupModal] = useState<string | false>(false);
  const [deleteModalState, setDeleteModal] = useState<string | false>(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeInstructionsModal = () => setInstructionsModal(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeSetupModal = () => setSetupModal(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const closeDeleteModal = () => setDeleteModal(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showInstructionsModal = (location: "banner" | "list", inboxId?: string) => {
    trackInstructionsOpened(location);
    setInstructionsModal(inboxId ?? true);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showAddModal = () => {
    trackAddModalOpened();
    setSetupModal(nextEmail);
  };
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showEditModal = (inboxId: string) => {
    trackEditModalOpened();
    setSetupModal(inboxId);
  };
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showDeleteModal = (inboxId: string) => {
    trackDeleteModalOpened();
    setDeleteModal(inboxId);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const hideInstructionsModal = () => {
    closeInstructionsModal();
    history.push(EmailSettingsRoutes.ForwardingAddresses);
  };

  useEffect(() => {
    trackEmailSectionOpened(EmailSettingsSubsection.ForwardingAddresses);
    dispatch(SettingsViewActions.fetchNextForwardingAddress());
  }, []);

  useEffect(() => {
    if (hash === FORWARDING_INSTRUCTIONS_HASH) {
      setInstructionsModal(prepareMailboxAddress(licenseId));
    }

    if (hash.startsWith(FORWARDING_SETUP_HASH) && canManageInboxes) {
      setSetupModal(hash.substring(6));
    }
  }, [hash, licenseId, canManageInboxes]);

  return (
    <>
      <ForwardingInfo onReadForwardingInstructions={showInstructionsModal.bind(null, "banner")} />
      <Flex gap={withVar(SpacingToken.Spacing6)} column>
        <Flex column gap={withVar(SpacingToken.Spacing8)}>
          {!canManageInboxes && <NotAllowedBanner section={EmailSettingsSubsection.ForwardingAddresses} />}
          <Block>
            <Button
              onClick={showAddModal}
              kind="secondary"
              disabled={!canManageInboxes}
              data-testid="add-forwarding-address-button"
            >
              Add forwarding address
            </Button>
          </Block>
          <ForwardingAddressList
            onDeleteInbox={showDeleteModal}
            onEditInbox={showEditModal}
            onShowInstructions={showInstructionsModal.bind(null, "list")}
          />
        </Flex>
        {!hasClosedTip && <ForwardingAddressesTip />}
      </Flex>
      {instructionsModalState && (
        <InstructionsModal
          onClose={hideInstructionsModal}
          address={typeof instructionsModalState === "string" ? instructionsModalState : undefined}
        />
      )}
      {setupModalState && (
        <SetupInboxModal onClose={closeSetupModal} onInboxSetup={noop} editingInboxID={setupModalState} />
      )}
      {deleteModalState && <DeleteInboxModal onClose={closeDeleteModal} inboxId={deleteModalState} />}
    </>
  );
};
